import { FrontAgent as CoreFrontAgent } from '@firesim/simkit-core';
import type { Plane, FireCell } from '@firesim/simkit-core';
import { getConstants, getFireState } from '@firesim/simkit-core';
import type { Terrain } from '@firesim/simkit-core';
import { getDirectionVectorFromCoords } from '@firesim/simkit-core';
import type { TerrainBase } from '@firesim/simkit-core';
import { DarwinEmberAgent } from './darwin-ember-agent';
// Legacy wind behaviour (from original app.js sources)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { getNeighbourDirections as legacyGetNeighbourDirections, calcWindInfluence as legacyCalcWindInfluence, calcThrowDistance as legacyCalcThrowDistance } from '../old/calc-wind.js';

// Reintroduce the original “surrounding heat” contribution while using the modern wind model.

export type DarwinFrontAgentSettings<TCodes extends TerrainBase> = {
  terrainTypes: Record<TCodes, Terrain<TCodes>>;
  protectedTerrain?: Array<TCodes>;
};

export class DarwinFrontAgent<TCodes extends TerrainBase> extends CoreFrontAgent<TCodes> {
  constructor(plane: Plane<TCodes, FireCell<TCodes>>, settings: DarwinFrontAgentSettings<TCodes>) {
    super(plane, settings);
  }

  run(): void {
    // Override to apply legacy burn probability with surrounding heat and legacy-style wind scalar.
    if (!this.cell) return;

    const state = getFireState(this.plane);
    const { agents, wind: windConstants } = getConstants();
    const constants = agents.frontAgent;

    // Draw current cell
    this.plane.drawCell(this.cell.x, this.cell.y, 255, 255, 0, 255, { layer: 'fire' });

    // Legacy-style wind influence propagation: set neighbour.wind by ring and direction
    this.applyLegacyWindScalar();

    const neighbours: FireCell<TCodes>[] = this.plane.getNeighbours(this.cell.x, this.cell.y);
    const burnChance = Math.random() * constants.burnChanceRange;

    neighbours.forEach((neighbour) => {
      if (!neighbour || neighbour.isBurned) return;

      // Surrounding heat from burning neighbours (legacy behaviour)
      const surroundingFires = this.plane
        .getNeighbours(neighbour.x, neighbour.y)
        .reduce((acc, s) => acc + (s.getAgents().length > 0 ? 1 : 0), 0);
      const surroundingHeat = (surroundingFires * constants.surroundingMultiplier) + constants.surroundingAddition;

      // Fire danger (include optional runtime adjustment if present)
      const fireDanger = state.fireDanger * (state.fireDangerAdjustment || 1);

      const burnability = neighbour.fuelLoad * (
        ((fireDanger ** 2) * constants.fuelLoadMultiplier) +
        (fireDanger * constants.fireDangerMultiplier) +
        constants.fireDangerAddition
      ) * surroundingHeat;

      // Use legacy scalar wind value written to neighbour.wind above
      const burnProb = burnability;
      neighbour.burnProb = burnProb * neighbour.wind;

      if (burnChance <= neighbour.burnProb) {
        this.ignite(neighbour);
      }
    });

    // Simple legacy throw distance behaviour
    this.throwSpotLegacy(burnChance, this.cell);

    // Transition to EmberAgent
    this.plane.removeAgentFromCell(this, this.cell);
    // Use DarwinEmberAgent (thin wrapper) so we can tweak later if needed.
    this.plane.addAgentToCell(new DarwinEmberAgent(this.plane, this.settings), this.cell);
  }

  private applyLegacyWindScalar(): void {
    if (!this.cell) return;
    const state = getFireState(this.plane);
    const windDir = this.toGridDirection(state.windDirectionVector);
    if (windDir[0] === 0 && windDir[1] === 0) return;

    const maxStrength = 4; // legacy algorithm uses rings 0..4
    for (let distance = 0; distance <= maxStrength; distance++) {
      const offsets: Array<[number, number]> = legacyGetNeighbourDirections(windDir, distance) as Array<[number,number]>;
      const cellWind = legacyCalcWindInfluence(distance, state.windSpeed);
      offsets.forEach(([ox, oy]: [number, number]) => {
        const x = this.cell!.x + ox;
        const y = this.cell!.y + oy;
        if (x >= 0 && x < this.plane.width && y >= 0 && y < this.plane.height) {
          const target = this.plane.getCell(x, y);
          if (target) {
            target.wind = cellWind;
          }
        }
      });
    }
  }

  // Convert arbitrary vector to grid direction (-1/0/1)
  private toGridDirection(vec: [number, number] | number[]): [number, number] {
    const [x, y] = vec as number[];
    const mag = Math.hypot(x, y) || 1;
    const ux = Math.round((x / mag));
    const uy = Math.round((y / mag));
    return [Math.max(-1, Math.min(1, ux)), Math.max(-1, Math.min(1, uy))];
  }

  private throwSpotLegacy(burnChance: number, cell: FireCell<TCodes>): void {
    const state = getFireState(this.plane);
    const windDir = this.toGridDirection(state.windDirectionVector);
    const distance = legacyCalcThrowDistance(state.windSpeed) * 2;
    if (distance <= 0) return;
    const target = this.plane.getCell(cell.x + windDir[0] * distance, cell.y + windDir[1] * distance);
    if (target && burnChance < cell.burnProb) {
      this.ignite(target);
    }
  }
}

export default DarwinFrontAgent;
